import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState, type FormEvent, type MouseEvent, type ReactNode } from 'react'
import { ApiError } from '../../api/client'
import {
  addServiceTicketType,
  contractDocumentUrl,
  contractDocumentsQuery,
  contractNumberQuery,
  contractServicesQuery,
  contractsQuery,
  contractsShellQuery,
  generalSettingsQuery,
  isContractTab,
  propertyFormQuery,
  removeContractDocument,
  removeMeasurementUnit,
  removeServiceProperty,
  removeServiceTicketType,
  saveContract,
  saveContractType,
  saveService,
  saveServiceProperty,
  serviceFormQuery,
  servicePropertiesQuery,
  serviceTicketTypesQuery,
  servicesQuery,
  setContractTypeStatus,
  storeContractDocument,
  ticketTypeChoicesQuery,
  toggleService,
  type ContractRow,
  type ContractTypeList,
  type ContractTypeRow,
  type ContractsShell,
  type ContractsTableRequest,
  type PropertyRow,
  type ServiceOption,
  type ServiceRow,
} from '../../api/settingsContracts'
import {
  contractFormServicesQuery,
  contractTemplateChoicesQuery,
} from '../../api/settingsContractTemplates'
import { DataTable, useTableState, type TableColumn, type TableState } from '../../ui/DataTable'
import { Forbidden } from '../../ui/Forbidden'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings -> Contracts (`settings/customersV2/contracts`).
 *
 * Bara de file e cea din `customersV2/tabList.php`, in aceeasi ordine, si
 * fiecare fila e macheta ei, element cu element:
 *
 *   General Settings     `customersV2/contractServiceSettings.php`
 *   Contracts Templates  duce la ecranul propriu (`/settings/contracts_templates`)
 *   Services             `customersV2/serviceList/index.php` + `serviceList.js`
 *   Service Properties   `customersV2/serviceList/service_properties.php`
 *   Customers Contracts  `contracts/new/allContracts.php` + `contracts/contracts.js`
 *
 * CE E ALTFEL, SI DE CE
 * ---------------------
 * RANDUL DESFASURAT AL UNUI CONTRACT ARATA SI SERVICIILE, SI DOCUMENTELE. In
 * portal erau doua butoane care deschideau, fiecare, acelasi rand-copil cu alt
 * continut; al doilea clic pe celalalt buton il inchidea pe primul, deci nu se
 * puteau vedea decat pe rand. Aici randul se deschide o data si arata
 * amandoua sectiunile - a doua numai cu `settings.contract_documents.read`,
 * exact ca butonul vechi.
 *
 * FEREASTRA DE CONTRACT NU MAI ARE CASUTA DE DOCUMENT. In portal, formularul
 * de contract urca un document ca urmare a salvarii
 * (`newUpdateContract` chema `storeDocument`), iar campurile erau marcate cu
 * stelute rosii desi validarea lor era comentata. Documentele se adauga acum
 * din randul desfasurat, unde stau si cele vechi - acelasi capat, acelasi
 * fisier, un singur loc.
 */
const SERVICES_TABLE = 'settings-contract-services'

const PROPERTIES_TABLE = 'settings-contract-properties'

const CONTRACTS_TABLE = 'settings-contracts'

/** Ce s-a intamplat, spus cu vorbele SERVERULUI cand le are. */
function failed(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'Something went wrong.'
}

export function esc(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/** Cererea de lista, din starea tabelului. */
function tableRequest(state: TableState, names: string[], fallback: string): ContractsTableRequest {
  return {
    start: state.start,
    length: state.length,
    orderColumn: state.order.column,
    orderName: names[state.order.column] ?? fallback,
    orderDir: state.order.dir,
    search: state.search,
  }
}

/** `moment(...).format('LLL')` din modelele de lista vechi, pe scurt. */
export function whenText(value: string): string {
  if (!value || value.startsWith('0000')) return ''
  const when = new Date(value.replace(' ', 'T'))
  if (Number.isNaN(when.getTime())) return value
  return when.toLocaleString()
}

/* =========================================================================
 * Cadrul
 * ====================================================================== */

export function ContractsSettingsPage({ tab }: { tab: string }) {
  const navigate = useNavigate()
  const shell = useQuery(contractsShellQuery)

  useEffect(() => {
    if (!shell.data) return
    if (tab !== '' && isContractTab(tab)) return
    void navigate({ to: '/settings/customersV2/contracts/$tab', params: { tab: 'contractServiceSettings' } })
  }, [shell.data, tab, navigate])

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (!shell.data) return <Spinner />

  const current = isContractTab(tab) ? tab : 'contractServiceSettings'

  return (
    <>
      <nav className="od-tabbar">
        <ul className="od-tabbar-nav">
          <li className="menu-header">Contracts</li>
          {shell.data.tabs.map((item) => (
            <SidebarTab key={item.id} tab={item} current={current} />
          ))}
          <li className="menu-block-divider"></li>
        </ul>
      </nav>
      <div className="page-content-wrapper">
        {current === 'contractServiceSettings' && <GeneralSettingsPanel />}
        {current === 'serviceList' && <ServicesPanel shell={shell.data} />}
        {current === 'service_properties' && <ServicePropertiesPanel />}
        {current === 'allContracts' && <ContractsPanel shell={shell.data} />}
      </div>
    </>
  )
}

function SidebarTab({ tab, current }: { tab: ContractsShell['tabs'][number]; current: string }) {
  const navigate = useNavigate()
  const active = tab.id === current
  const href = tab.to !== '' ? '/app' + tab.to : '/app/settings/customersV2/contracts/' + tab.id
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    if (tab.to !== '') {
      void navigate({ to: tab.to })
      return
    }
    void navigate({ to: '/settings/customersV2/contracts/$tab', params: { tab: tab.id } })
  }
  return (
    <li className={active ? 'menu-tab active' : 'menu-tab'}>
      <a id={tab.id} href={href} onClick={onClick}>
        {tab.label}
      </a>
    </li>
  )
}

/* =========================================================================
 * General Settings (contractServiceSettings.php)
 * ====================================================================== */

/** Ce se editeaza acum intr-una din cele opt liste. */
type TypeDraft = {
  kind: string
  label: string
  shape: ContractTypeList['shape']
  row: ContractTypeRow | null
}

function GeneralSettingsPanel() {
  const query = useQueryClient()
  const page = useQuery(generalSettingsQuery)
  const [draft, setDraft] = useState<TypeDraft | null>(null)

  const reload = () => void query.invalidateQueries({ queryKey: ['settings', 'contracts', 'general'] })

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  const data = page.data

  return (
    <div id="content">
      <h4>Contract/Service settings</h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage, create or edit contract and services settings.
      </label>
      <hr />

      {data.lists.map((list) => (
        <div className="row settings-category" key={list.kind}>
          <div className="col-xs-12">
            <h5>
              {list.label}
              {data.canCreate && (
                <button
                  type="button"
                  className="btn btn-xs btn-success pull-right"
                  onClick={() => setDraft({ kind: list.kind, label: list.label, shape: list.shape, row: null })}
                >
                  <i className="fas fa-plus" aria-hidden="true"></i> Add {list.label.replace(/s$/, '').toLowerCase()}
                </button>
              )}
            </h5>
          </div>
          <div className="col-xs-12">
            {list.rows.length === 0 ? (
              <p className="alert">No {list.label.toLowerCase()} defined</p>
            ) : (
              <ul className="list-group contract_hoverable_children">
                {list.rows.map((row) => (
                  <li className="list-group-item" key={row.ID}>
                    <i className="fas fa-check-circle"></i>&nbsp;&nbsp;{row.name}
                    {list.shape === 'minutes' && (
                      <span className="contracts_option_small">
                        {' '}
                        &mdash; {row.calc_days ?? 0}d {row.calc_hours ?? 0}h {row.calc_minutes ?? 0}m
                      </span>
                    )}
                    {list.shape === 'flags' && row.has_quantity === 1 && (
                      <span className="contracts_option_small"> &mdash; Has quantity</span>
                    )}
                    {data.canEdit && (
                      <button
                        type="button"
                        className={
                          row.status === '0'
                            ? 'btn btn-xs btn-success pull-right'
                            : 'btn btn-xs btn-danger pull-right'
                        }
                        onClick={() => {
                          void setContractTypeStatus(list.kind, row.ID, row.status === '0' ? 1 : 0)
                            .then(reload)
                            .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
                        }}
                      >
                        {row.status === '0' ? 'Enable' : 'Disable'}
                      </button>
                    )}
                    {data.canEdit && (
                      <button
                        type="button"
                        className="btn btn-xs btn-primary pull-right"
                        onClick={() => setDraft({ kind: list.kind, label: list.label, shape: list.shape, row })}
                      >
                        Edit
                      </button>
                    )}
                    {data.canEdit && list.kind === 'measurement_unit_type' && (
                      <button
                        type="button"
                        className="btn btn-xs btn-danger pull-right"
                        style={{ marginRight: 5 }}
                        onClick={() => {
                          if (!confirm('Are you sure? The measurement unit will be removed.')) return
                          void removeMeasurementUnit(row.ID)
                            .then(() => {
                              toast('Measurement unit removed.', 'Success')
                              reload()
                            })
                            .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
                        }}
                      >
                        <i className="fas fa-trash" aria-hidden="true"></i>
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ))}

      <TypeModal
        draft={draft}
        categoryTypes={data.categoryTypes}
        onClose={() => setDraft(null)}
        onSaved={() => {
          setDraft(null)
          reload()
        }}
      />
    </div>
  )
}

/**
 * Una singura pentru toate cele opt ferestre din `contractServiceSettings.php`:
 * aveau acelasi trup, si numai cele de SLA aveau in plus zile / ore / minute,
 * iar unitatile de masura bifele lor.
 */
function TypeModal({
  draft,
  categoryTypes,
  onClose,
  onSaved,
}: {
  draft: TypeDraft | null
  categoryTypes: string[]
  onClose(): void
  onSaved(): void
}) {
  const [name, setName] = useState('')
  const [days, setDays] = useState('0')
  const [hours, setHours] = useState('0')
  const [minutes, setMinutes] = useState('0')
  const [hasQuantity, setHasQuantity] = useState(false)
  const [hasCategory, setHasCategory] = useState(false)
  const [category, setCategory] = useState('')

  useEffect(() => {
    const row = draft?.row ?? null
    setName(row?.name ?? '')
    setDays(String(row?.calc_days ?? 0))
    setHours(String(row?.calc_hours ?? 0))
    setMinutes(String(row?.calc_minutes ?? 0))
    setHasQuantity((row?.has_quantity ?? 0) === 1)
    setHasCategory((row?.has_category ?? 0) === 1)
    setCategory(row?.category_type ?? '')
  }, [draft])

  if (draft === null) {
    return <Modal id="contractTypeModal" open={false} onClose={onClose} title="" children={null} />
  }

  const editing = draft.row !== null

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (name.trim() === '') return
    void saveContractType(draft.kind, {
      id: draft.row?.ID ?? 0,
      name: name.trim(),
      days,
      hours,
      minutes,
      has_quantity: hasQuantity ? 'on' : '',
      has_category: hasCategory ? 'on' : '',
      category_type: category,
    })
      .then(() => {
        toast(editing ? 'Settings saved.' : 'Added.', 'Success')
        onSaved()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
  }

  return (
    <Modal
      id="contractTypeModal"
      labelledBy="contractTypeModalLabel"
      open
      onClose={onClose}
      title={(editing ? 'Edit ' : 'Add new ') + draft.label.replace(/s$/, '').toLowerCase()}
    >
      <form className="form-horizontal labelcustomize" method="post" onSubmit={submit}>
        <div className="form-group">
          <label className="col-sm-4 control-label" style={{ textAlign: 'left' }}>
            <span className="color">Name</span>
          </label>
          <div className="col-sm-8">
            <input className="form-control" name="name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
        </div>

        {draft.shape === 'minutes' && (
          <>
            <NumberField label="Days" value={days} onChange={setDays} />
            <NumberField label="Hours" value={hours} onChange={setHours} />
            <NumberField label="Minutes" value={minutes} onChange={setMinutes} />
          </>
        )}

        {draft.shape === 'flags' && (
          <>
            <div className="form-group">
              <label className="col-sm-5 control-label" style={{ textAlign: 'left' }}>
                <span className="color">Quantity enabled</span>
              </label>
              <div className="col-sm-7">
                <input type="checkbox" checked={hasQuantity} onChange={(e) => setHasQuantity(e.target.checked)} />
              </div>
            </div>
            <div className="form-group">
              <label className="col-sm-5 control-label" style={{ textAlign: 'left' }}>
                <span className="color">Category enabled</span>
              </label>
              <div className="col-sm-7">
                <input type="checkbox" checked={hasCategory} onChange={(e) => setHasCategory(e.target.checked)} />
              </div>
            </div>
            <div className="form-group">
              <label className="col-sm-5 control-label" style={{ textAlign: 'left' }}>
                <span className="color">Category</span>
              </label>
              <div className="col-sm-7">
                <select
                  className="form-control"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  disabled={!hasCategory}
                >
                  <option value="">Select</option>
                  {categoryTypes.map((item) => (
                    <option key={item} value={item}>
                      {item.charAt(0).toUpperCase() + item.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}

        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button type="submit" className="btn btn-primary" disabled={name.trim() === ''}>
            {editing ? 'Edit' : 'Add'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function NumberField({ label, value, onChange }: { label: string; value: string; onChange(v: string): void }) {
  return (
    <div className="form-group">
      <label className="col-sm-4 control-label" style={{ textAlign: 'left' }}>
        <span className="color">{label}</span>
      </label>
      <div className="col-sm-8">
        <input
          className="form-control"
          type="number"
          min={0}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </div>
  )
}

/* =========================================================================
 * Services (serviceList)
 * ====================================================================== */

export const SERVICE_ORDER_NAMES = [
  'ID',
  'service_name',
  'service_category_id',
  'priority_name',
  'coverage_name',
  'sla_name',
  'intervention_sla_name',
  'resolve_sla_name',
  'measurement_unit_name',
  'has_quantity',
  'estimate_unit_cost',
  'internal_cost',
  'quantity',
  '',
]

export function serviceColumns(interventionLabel: string, canEdit: boolean): TableColumn[] {
  const text = (index: number, title: string, key: keyof ServiceRow): TableColumn => ({
    index,
    title,
    orderable: true,
    visible: true,
    html: (row) => esc(String(row[key] ?? '')),
  })

  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 30,
      html: (row) =>
        `<center class="center show_details color-blue pointer font-16" data-id="${Number(row.ID)}"><i class="fa fa-eye"></i></center>`,
    },
    {
      index: 1,
      title: 'Service Name',
      orderable: true,
      visible: true,
      html: (row) => `<strong style="color:#2b6497">${esc(String(row.service_name ?? ''))}</strong>`,
    },
    text(2, 'Service Category', 'service_category_name'),
    text(3, 'Priority', 'priority_name'),
    text(4, 'Coverage', 'coverage_name'),
    text(5, 'Response Sla', 'sla_name'),
    text(6, `${interventionLabel} SLA`, 'intervention_sla_name'),
    text(7, 'Resolve Sla', 'resolve_sla_name'),
    text(8, 'MeasurementUnit', 'measurement_unit_name'),
    {
      index: 9,
      title: 'Has Quantity',
      orderable: true,
      visible: true,
      html: (row) =>
        Number(row.has_quantity) === 1
          ? '<i class="fa fa-check fa-2x general_green"></i>'
          : '<i class="fas fa-times fa-2x general_red"></i>',
    },
    {
      index: 10,
      title: 'Estimate Unit Cost',
      orderable: true,
      visible: true,
      html: (row) =>
        Number(row.estimate_unit_cost) > 0
          ? esc(String(row.estimate_unit_cost) + ' ' + String(row.currency ?? ''))
          : 'N/A',
    },
    {
      index: 11,
      title: 'Internal Cost',
      orderable: true,
      visible: true,
      html: (row) =>
        Number(row.internal_cost) > 0 ? esc(String(row.internal_cost) + ' ' + String(row.currency ?? '')) : 'N/A',
    },
    text(12, 'Quantity', 'quantity'),
    {
      index: 13,
      title: 'Action',
      orderable: false,
      visible: true,
      html: (row) => {
        if (!canEdit) return ''
        const id = Number(row.ID)
        const enabled = Number(row.status) === 1
        return (
          `<button class="btn btn-xs btn-primary od-edit-service" data-id="${id}">Edit</button>&nbsp;&nbsp;&nbsp;` +
          `<button class="btn btn-xs ${enabled ? 'btn-danger' : 'btn-success'} od-toggle-service" data-id="${id}">` +
          `<i class="fas ${enabled ? 'fa-times-circle' : 'fa-undo'}" aria-hidden="true"></i> ${enabled ? 'Disable' : 'Enable'}</button>`
        )
      },
    },
  ]
}

function ServicesPanel({ shell }: { shell: ContractsShell }) {
  const query = useQueryClient()
  const form = useQuery(serviceFormQuery)
  const [editing, setEditing] = useState<ServiceRow | null>(null)
  const [creating, setCreating] = useState(false)
  const [state, setState] = useTableState(SERVICES_TABLE, {
    start: 0,
    length: 25,
    /* `order: [[1, 'desc']]` din `ServiceTemplates.initDatatable`. */
    order: { column: 1, dir: 'desc' },
    search: '',
  })

  const table = useQuery({
    ...servicesQuery(tableRequest(state, SERVICE_ORDER_NAMES, 'ID')),
    placeholderData: keepPreviousData,
  })

  const columns = useMemo(
    () => serviceColumns(shell.interventionLabel, shell.canEdit),
    [shell.interventionLabel, shell.canEdit],
  )

  if (table.error instanceof ApiError && table.error.status === 403) return <Forbidden />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]
  const reload = () => void query.invalidateQueries({ queryKey: ['settings', 'contracts', 'services'] })

  return (
    <div id="content">
      <div className="row">
        <div className="col-xs-12">
          <h5>
            Service
            {shell.canCreate && (
              <button type="button" className="btn btn-xs btn-success pull-right" onClick={() => setCreating(true)}>
                <i className="fas fa-plus" aria-hidden="true"></i> Add Service
              </button>
            )}
          </h5>
          <label className="color" style={{ marginBottom: 0 }}>
            Manage, create or edit services
          </label>
        </div>
      </div>
      <hr />
      <div
        onClick={(event) => {
          const target = event.target as HTMLElement
          const edit = target.closest('.od-edit-service')
          if (edit) {
            const id = Number(edit.getAttribute('data-id') ?? 0)
            const found = (table.data?.data ?? []).find((row) => row.ID === id) ?? null
            setEditing(found)
            return
          }
          const toggle = target.closest('.od-toggle-service')
          if (!toggle) return
          const id = Number(toggle.getAttribute('data-id') ?? 0)
          void toggleService(id)
            .then((answer) => {
              toast(answer.status === 'disabled' ? 'Service disabled.' : 'Service enabled.', 'Success')
              reload()
            })
            .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
        }}
      >
        <DataTable
          id={SERVICES_TABLE}
          columns={columns}
          rows={rows}
          total={table.data?.recordsFiltered ?? 0}
          fetching={table.isFetching}
          error={table.error ? table.error.message : null}
          onRetry={() => void table.refetch()}
          state={state}
          onState={setState}
          rowId={(row) => String(row.DT_RowId ?? `row_${Number(row.ID)}`)}
          emptyHint="A service is one line of what a contract promises."
          details={(row) => <ServiceTicketTypesPanel serviceId={Number(row.ID)} />}
        />
      </div>

      {form.data && (
        <ServiceModal
          open={creating || editing !== null}
          service={editing}
          form={form.data}
          onClose={() => {
            setCreating(false)
            setEditing(null)
          }}
          onSaved={() => {
            setCreating(false)
            setEditing(null)
            reload()
          }}
        />
      )}
    </div>
  )
}

/** `serviceList/tickets.php`: felurile de tichet legate de un serviciu. */
function ServiceTicketTypesPanel({ serviceId }: { serviceId: number }) {
  const query = useQueryClient()
  const page = useQuery(serviceTicketTypesQuery(serviceId))
  const [search, setSearch] = useState('')
  const [chosen, setChosen] = useState(0)
  const choices = useQuery(ticketTypeChoicesQuery(search))

  const reload = () =>
    void query.invalidateQueries({ queryKey: ['settings', 'contracts', 'services', serviceId, 'ticket_types'] })

  if (!page.data) return <Spinner />

  return (
    <div className="row d-flex m-0">
      <div className="col-md-12">
        <div className="details_content">
          <h5>Edit ticket types</h5>
          {page.data.canEdit && (
            <div className="row settings-category">
              <div className="col-md-5">
                <input
                  className="form-control"
                  placeholder="Please enter ticket type name"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <select
                  className="form-control"
                  value={chosen}
                  onChange={(e) => setChosen(Number(e.target.value))}
                >
                  <option value={0}>Select a ticket type</option>
                  {(choices.data?.rows ?? []).map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.text}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={chosen < 1}
                  onClick={() => {
                    void addServiceTicketType(serviceId, chosen)
                      .then((answer) => {
                        if (answer.status === 'success') {
                          toast('Ticket type added.', 'Success')
                          setChosen(0)
                          reload()
                        } else {
                          toast('Ticket type already assigned.', 'Error', 'error')
                        }
                      })
                      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
                  }}
                >
                  Add
                </button>
              </div>
            </div>
          )}

          {page.data.rows.length === 0 ? (
            <p className="alert">No ticket types assigned</p>
          ) : (
            page.data.rows.map((item) => (
              <div className="row settings-category service_ticket_list" key={item.id}>
                <div className="col-md-1">
                  <span
                    className="badge ticket-type-badge"
                    title={item.typeName}
                    style={{ backgroundColor: item.color }}
                  >
                    {item.shortName}
                  </span>
                </div>
                <div className="col-md-1">{item.id}</div>
                <div className="col-md-7">{item.typeName}</div>
                <div className="col-md-3 pull-right">
                  {page.data.canEdit && (
                    <button
                      type="button"
                      className="btn btn-xs btn-danger"
                      onClick={() => {
                        void removeServiceTicketType(serviceId, item.id)
                          .then(() => {
                            toast('Ticket type removed.', 'Success')
                            reload()
                          })
                          .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
                      }}
                    >
                      <i className="fas fa-times-circle" aria-hidden="true"></i> Delete
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

function Choice({ options, value, onChange, empty = 'Select' }: {
  options: ServiceOption[]
  value: number
  onChange(v: number): void
  empty?: string
}) {
  return (
    <select className="form-control" value={value} onChange={(e) => onChange(Number(e.target.value))}>
      <option value={0}>{empty}</option>
      {options.map((item) => (
        <option key={item.ID} value={item.ID}>
          {item.name}
        </option>
      ))}
    </select>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="form-group">
      <label className="col-sm-4 control-label" style={{ textAlign: 'left' }}>
        <span className="color">{label}</span>
      </label>
      <div className="col-sm-8">{children}</div>
    </div>
  )
}

/** `serviceList/index.php`, fereastra "Add/Edit Service Template". */
function ServiceModal({
  open,
  service,
  form,
  onClose,
  onSaved,
}: {
  open: boolean
  service: ServiceRow | null
  form: NonNullable<ReturnType<typeof useQuery<import('../../api/settingsContracts').ServiceForm>>['data']>
  onClose(): void
  onSaved(): void
}) {
  const [name, setName] = useState('')
  const [category, setCategory] = useState(0)
  const [priority, setPriority] = useState(0)
  const [property, setProperty] = useState(0)
  const [unit, setUnit] = useState(0)
  const [cost, setCost] = useState('0.00')
  const [currency, setCurrency] = useState('')
  const [internal, setInternal] = useState('')
  const [description, setDescription] = useState('')

  useEffect(() => {
    if (!open) return
    setName(service?.service_name ?? '')
    setCategory(service?.service_category_id ?? 0)
    setPriority(service?.priority_id ?? 0)
    setProperty(service?.property_id ?? 0)
    setUnit(service?.measurement_unit_id ?? 0)
    setCost(service?.estimate_unit_cost ?? '0.00')
    setCurrency(service?.currency ?? '')
    setInternal(service?.internal_cost ?? '')
    setDescription(service?.description ?? '')
  }, [open, service])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    void saveService({
      service_template_id: service?.ID ?? 0,
      service_template_name: name.trim(),
      service_category_id: category,
      service_template_priority: priority,
      service_properties: property,
      service_template_measurement_unit: unit,
      service_template_estimated_unit_cost: cost,
      service_template_currency: currency,
      service_template_internal_cost: internal,
      service_desc: description,
    })
      .then(() => {
        toast(service ? 'Service saved.' : 'Service added.', 'Success')
        onSaved()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
  }

  return (
    <Modal
      id="modalServiceTemplate"
      labelledBy="modalServiceTemplateLabel"
      open={open}
      onClose={onClose}
      title={service ? 'Edit Service template' : 'Add new Service template'}
    >
      <form className="form-horizontal labelcustomize" method="post" onSubmit={submit}>
        <Field label="Service Name">
          <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Service Category">
          <Choice options={form.serviceCategories} value={category} onChange={setCategory} />
        </Field>
        <Field label="Priority">
          <Choice options={form.priorities} value={priority} onChange={setPriority} />
        </Field>
        <Field label="Properties">
          <Choice options={form.properties.map((p) => ({ ID: p.id, name: p.name }))} value={property} onChange={setProperty} />
        </Field>
        <Field label="Measurement Unit">
          <Choice options={form.measurementUnits} value={unit} onChange={setUnit} />
        </Field>
        <Field label="Estimated unit cost">
          <input className="form-control" value={cost} onChange={(e) => setCost(e.target.value)} />
        </Field>
        <Field label="Currency">
          <select className="form-control" value={currency} onChange={(e) => setCurrency(e.target.value)}>
            <option value="">Select</option>
            {form.currencies.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Internal cost">
          <input className="form-control" value={internal} onChange={(e) => setInternal(e.target.value)} />
        </Field>
        <Field label="Description">
          <textarea
            className="form-control"
            style={{ maxWidth: '100%' }}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button
            type="submit"
            className="btn btn-primary"
            disabled={name.trim() === '' || category < 1 || priority < 1 || unit < 1}
          >
            {service ? 'Save' : 'Add'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

/* =========================================================================
 * Service Properties
 * ====================================================================== */

export const PROPERTY_ORDER_NAMES = [
  'name',
  'coverage',
  'response_sla',
  'intervention_sla',
  'resolve_sla',
  'user_fullname',
  'created_at',
  '',
]

export function propertyColumns(canEdit: boolean): TableColumn[] {
  const text = (index: number, title: string, key: keyof PropertyRow): TableColumn => ({
    index,
    title,
    orderable: true,
    visible: true,
    className: 'a-left',
    html: (row) => esc(String(row[key] ?? '')),
  })

  return [
    text(0, 'Name', 'name'),
    text(1, 'Coverage', 'coverage'),
    text(2, 'Response SLA', 'response_sla'),
    text(3, 'Intervention SLA', 'intervention_sla'),
    text(4, 'Resolve SLA', 'resolve_sla'),
    text(5, 'Created By', 'user_fullname'),
    text(6, 'Created At', 'created_at'),
    {
      index: 7,
      title: 'Actions',
      orderable: false,
      visible: true,
      className: 'a-right',
      html: (row) => {
        if (!canEdit) return ''
        const id = Number(row.id)
        return (
          `<button class="btn btn-xs btn-primary od-edit-property" data-id="${id}">Edit</button>&nbsp;&nbsp;` +
          `<button class="btn btn-xs btn-danger od-remove-property" data-id="${id}">` +
          '<i class="fas fa-times-circle" aria-hidden="true"></i> Delete</button>'
        )
      },
    },
  ]
}

function ServicePropertiesPanel() {
  const query = useQueryClient()
  const form = useQuery(propertyFormQuery)
  const [editing, setEditing] = useState<PropertyRow | null>(null)
  const [creating, setCreating] = useState(false)
  const [state, setState] = useTableState(PROPERTIES_TABLE, {
    start: 0,
    length: 25,
    order: { column: 0, dir: 'desc' },
    search: '',
  })
  const table = useQuery({
    ...servicePropertiesQuery(tableRequest(state, PROPERTY_ORDER_NAMES, 'id')),
    placeholderData: keepPreviousData,
  })
  const columns = useMemo(() => propertyColumns(form.data?.canEdit ?? false), [form.data?.canEdit])

  if (table.error instanceof ApiError && table.error.status === 403) return <Forbidden />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]
  const reload = () => void query.invalidateQueries({ queryKey: ['settings', 'contracts', 'service_properties'] })

  return (
    <div id="content">
      {form.data?.canCreate && (
        <button type="button" className="pull-right btn btn-success btn-sm" onClick={() => setCreating(true)}>
          <i className="fas fa-plus" aria-hidden="true"></i> New Service Property
        </button>
      )}
      <h4>
        <strong>Services Properties</strong>
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage, create or edit <strong>properties</strong>
      </label>
      <hr />
      <div
        onClick={(event) => {
          const target = event.target as HTMLElement
          const edit = target.closest('.od-edit-property')
          if (edit) {
            const id = Number(edit.getAttribute('data-id') ?? 0)
            setEditing((table.data?.data ?? []).find((row) => row.id === id) ?? null)
            return
          }
          const remove = target.closest('.od-remove-property')
          if (!remove) return
          const id = Number(remove.getAttribute('data-id') ?? 0)
          if (id < 1 || !confirm('Are you sure? The property will be removed.')) return
          void removeServiceProperty(id)
            .then(() => {
              toast('Property was removed', 'Success')
              reload()
            })
            .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
        }}
      >
        <DataTable
          id={PROPERTIES_TABLE}
          columns={columns}
          rows={rows}
          total={table.data?.recordsFiltered ?? 0}
          fetching={table.isFetching}
          error={table.error ? table.error.message : null}
          onRetry={() => void table.refetch()}
          state={state}
          onState={setState}
          rowId={(row) => String(row.DT_RowId ?? `row_${Number(row.id)}`)}
          emptyHint="A property is the coverage and the three SLAs a service inherits."
        />
      </div>

      {form.data && (
        <PropertyModal
          open={creating || editing !== null}
          property={editing}
          form={form.data}
          onClose={() => {
            setCreating(false)
            setEditing(null)
          }}
          onSaved={() => {
            setCreating(false)
            setEditing(null)
            reload()
          }}
        />
      )}
    </div>
  )
}

function PropertyModal({
  open,
  property,
  form,
  onClose,
  onSaved,
}: {
  open: boolean
  property: PropertyRow | null
  form: NonNullable<ReturnType<typeof useQuery<import('../../api/settingsContracts').PropertyForm>>['data']>
  onClose(): void
  onSaved(): void
}) {
  const [name, setName] = useState('')
  const [coverage, setCoverage] = useState(0)
  const [response, setResponse] = useState(0)
  const [intervention, setIntervention] = useState(0)
  const [resolve, setResolve] = useState(0)

  useEffect(() => {
    if (!open) return
    setName(property?.name ?? '')
    setCoverage(property?.coverage_id ?? 0)
    setResponse(property?.response_sla_id ?? 0)
    setIntervention(property?.intervention_sla_id ?? 0)
    setResolve(property?.resolve_sla_id ?? 0)
  }, [open, property])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    void saveServiceProperty({
      id: property?.id ?? 0,
      property_name: name.trim(),
      property_coverage: coverage,
      response_sla: response,
      intervention_sla: intervention,
      resolve_sla: resolve,
    })
      .then(() => {
        toast('Service Properties saved!', 'Success')
        onSaved()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
  }

  return (
    <Modal
      id="propertyModal"
      labelledBy="propertyModalLabel"
      open={open}
      onClose={onClose}
      large
      title={property ? 'Edit Properties' : 'Add New Properties'}
    >
      <form method="post" onSubmit={submit}>
        <div className="row">
          <div className="col-md-9">
            <div className="form-group">
              <label className="control-label">
                Name <span className="required">*</span>
              </label>
              <input
                type="text"
                className="form-control input-sm"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
          </div>
          <div className="col-md-9">
            <div className="form-group">
              <label className="control-label">
                Coverage<span className="required">*</span>
              </label>
              <Choice options={form.coverages} value={coverage} onChange={setCoverage} empty=" - select - " />
            </div>
          </div>
          <div className="col-md-9">
            <div className="form-group">
              <label className="control-label">
                Response SLA<span className="required">*</span>
              </label>
              <Choice options={form.responseSla} value={response} onChange={setResponse} empty=" - select - " />
            </div>
          </div>
          <div className="col-md-9">
            <div className="form-group">
              <label className="control-label">
                Intervention SLA<span className="required">*</span>
              </label>
              <Choice
                options={form.interventionSla}
                value={intervention}
                onChange={setIntervention}
                empty=" - select - "
              />
            </div>
          </div>
          <div className="col-md-9">
            <div className="form-group">
              <label className="control-label">
                Resolve SLA<span className="required">*</span>
              </label>
              <Choice options={form.resolveSla} value={resolve} onChange={setResolve} empty=" - select - " />
            </div>
          </div>
          <div className="clearfix"></div>
          <div className="col-md-12">
            <div className="form-group aright">
              <button type="button" className="btn btn-default" onClick={onClose}>
                Close
              </button>{' '}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={name.trim() === '' || coverage < 1 || response < 1 || intervention < 1 || resolve < 1}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </form>
    </Modal>
  )
}

/* =========================================================================
 * Customers Contracts
 * ====================================================================== */

export const CONTRACT_ORDER_NAMES = [
  '',
  'company_name',
  'contract_number',
  'start_date',
  'end_date',
  'contract_type',
  'contract_template',
  'created',
  'description',
  '',
]

export function contractColumns(): TableColumn[] {
  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 30,
      html: (row) =>
        `<center class="center show_details color-blue pointer font-16" data-id="${Number(row.ID)}"><i class="fa fa-eye"></i></center>`,
    },
    { index: 1, title: 'Customer', orderable: true, visible: true, html: (row) => esc(String(row.company_name ?? '')) },
    {
      index: 2,
      title: 'Number',
      orderable: true,
      visible: true,
      html: (row) => esc(String(row.contract_number ?? '')),
    },
    {
      index: 3,
      title: 'Start date',
      orderable: true,
      visible: true,
      html: (row) => esc(whenText(String(row.start_date ?? ''))),
    },
    {
      index: 4,
      title: 'End date',
      orderable: true,
      visible: true,
      html: (row) => {
        const text = whenText(String(row.end_date ?? ''))
        return text === '' ? 'N/A' : esc(text)
      },
    },
    { index: 5, title: 'Type', orderable: true, visible: true, html: (row) => esc(String(row.contract_type ?? '')) },
    {
      index: 6,
      title: 'Template',
      orderable: true,
      visible: true,
      html: (row) => esc(String(row.contract_template ?? '')),
    },
    {
      index: 7,
      title: 'Created',
      orderable: true,
      visible: true,
      html: (row) => esc(whenText(String(row.created ?? ''))),
    },
    { index: 8, title: 'Description', orderable: true, visible: true, html: (row) => esc(String(row.description ?? '')) },
    {
      index: 9,
      title: 'Actions',
      orderable: false,
      visible: true,
      html: (row) =>
        Number(row.has_edit_access) === 1
          ? `<button class="btn btn-primary btn-xs pull-right od-edit-contract" data-id="${Number(row.ID)}"><i class="fas fa-pencil-alt" aria-hidden="true"></i> Edit</button>`
          : '',
    },
  ]
}

/**
 * Lista de contracte. Cu `company`, e fila "Contracts" din fisa clientului
 * (`Customer_contracts::index/{firma}`): aceleasi coloane, acelasi rand
 * desfasurat, numai contractele firmei.
 */
export function ContractsPanel({ shell, company }: { shell: ContractsShell; company?: number }) {
  const query = useQueryClient()
  const [editing, setEditing] = useState<ContractRow | null>(null)
  const [state, setState] = useTableState(company ? `${CONTRACTS_TABLE}-company` : CONTRACTS_TABLE, {
    start: 0,
    length: 25,
    /* `order: [[1, 'desc']]` din `ContactsPage.initDatatable`. */
    order: { column: 1, dir: 'desc' },
    search: '',
  })
  const table = useQuery({
    ...contractsQuery({ ...tableRequest(state, CONTRACT_ORDER_NAMES, 'ID'), company }),
    placeholderData: keepPreviousData,
  })
  const columns = useMemo(() => contractColumns(), [])

  if (table.error instanceof ApiError && table.error.status === 403) return <Forbidden />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]
  const reload = () => void query.invalidateQueries({ queryKey: ['settings', 'contracts', 'contracts'] })

  return (
    <div id="content">
      <h4>
        <strong>Customer Contracts</strong>
      </h4>
      <hr />
      <div
        onClick={(event) => {
          const target = event.target as HTMLElement
          const edit = target.closest('.od-edit-contract')
          if (!edit) return
          const id = Number(edit.getAttribute('data-id') ?? 0)
          setEditing((table.data?.data ?? []).find((row) => row.ID === id) ?? null)
        }}
      >
        <DataTable
          id={company ? `${CONTRACTS_TABLE}-company` : CONTRACTS_TABLE}
          columns={columns}
          rows={rows}
          total={table.data?.recordsFiltered ?? 0}
          fetching={table.isFetching}
          error={table.error ? table.error.message : null}
          onRetry={() => void table.refetch()}
          state={state}
          onState={setState}
          rowId={(row) => String(row.DT_RowId ?? `row_${Number(row.ID)}`)}
          emptyHint="A contract is what one customer company has bought."
          details={(row) => (
            <ContractDetails
              contractId={Number(row.ID)}
              companyId={Number(row.company_id)}
              canDocuments={Number(row.has_document_access) === 1}
            />
          )}
        />
      </div>

      {editing !== null && (
        <ContractModal
          contract={editing}
          shell={shell}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            reload()
          }}
        />
      )}
    </div>
  )
}

/**
 * Randul desfasurat: serviciile contractului si, cu dreptul de documente,
 * documentele lui. Vezi capul fisierului pentru de ce sunt amandoua deodata.
 */
function ContractDetails({
  contractId,
  companyId,
  canDocuments,
}: {
  contractId: number
  companyId: number
  canDocuments: boolean
}) {
  const services = useQuery(
    contractServicesQuery(contractId, {
      start: 0,
      length: 100,
      orderColumn: 1,
      orderName: 'service_name',
      orderDir: 'asc',
      search: '',
    }),
  )
  void companyId

  return (
    <div className="details_content">
      <h4>
        <strong>Contract Services</strong>
      </h4>
      {!services.data ? (
        <Spinner />
      ) : services.data.data.length === 0 ? (
        <p className="alert">No services on this contract</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table table-striped table-hover table-bordered" width="100%">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Priority</th>
                <th>Coverage</th>
                <th>Response SLA</th>
                <th>Intervention SLA</th>
                <th>Resolve SLA</th>
                <th>Measurement Unit</th>
                <th>Quantity</th>
                <th>Cost</th>
                <th>Remaining Units</th>
              </tr>
            </thead>
            <tbody>
              {services.data.data.map((row) => (
                <tr key={row.ID}>
                  <td>{row.service_name}</td>
                  <td>{row.service_category}</td>
                  <td>{row.priority}</td>
                  <td>{row.coverage}</td>
                  <td>{row.response_sla}</td>
                  <td>{row.intervention_sla}</td>
                  <td>{row.resolve_sla}</td>
                  <td>{row.measurement_unit}</td>
                  <td>{row.service_quantity}</td>
                  <td>{row.has_edit_access === 1 ? `${row.unit_cost} ${row.currency}` : 'N/A'}</td>
                  <td>{row.unit_remaining}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canDocuments && <ContractDocuments contractId={contractId} />}
    </div>
  )
}

/** `contracts/new/contractDocuments.php` + `addDocumentModal.php`. */
function ContractDocuments({ contractId }: { contractId: number }) {
  const query = useQueryClient()
  const [adding, setAdding] = useState(false)
  const documents = useQuery(
    contractDocumentsQuery(contractId, {
      start: 0,
      length: 100,
      orderColumn: 1,
      orderName: 'name',
      orderDir: 'asc',
      search: '',
    }),
  )

  const reload = () =>
    void query.invalidateQueries({ queryKey: ['settings', 'contracts', 'contracts', contractId, 'documents'] })

  return (
    <>
      <h4 style={{ marginTop: 20 }}>
        <strong>Contract Documents</strong>
        <button type="button" className="btn btn-success pull-right" onClick={() => setAdding(true)}>
          <i className="fas fa-plus" aria-hidden="true"></i> New document
        </button>
      </h4>
      {!documents.data ? (
        <Spinner />
      ) : documents.data.data.length === 0 ? (
        <p className="alert">No documents on this contract</p>
      ) : (
        <table className="table table-striped table-hover table-bordered" width="100%">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Created at</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {documents.data.data.map((row) => (
              <tr key={row.id}>
                <td>{row.id}</td>
                <td>{row.name}</td>
                <td>{row.created_at}</td>
                <td>
                  <a
                    className="btn btn-primary btn-xs"
                    href={contractDocumentUrl(contractId, row.id)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <i className="fa fa-download" aria-hidden="true"></i> Download
                  </a>{' '}
                  <button
                    type="button"
                    className="btn btn-danger btn-xs"
                    onClick={() => {
                      if (!confirm('Are you sure? The document will be removed.')) return
                      void removeContractDocument(contractId, row.id)
                        .then(() => {
                          toast('Document deleted!', 'Success')
                          reload()
                        })
                        .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
                    }}
                  >
                    <i className="fas fa-trash"></i> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <DocumentModal
        open={adding}
        contractId={contractId}
        onClose={() => setAdding(false)}
        onSaved={() => {
          setAdding(false)
          reload()
        }}
      />
    </>
  )
}

function DocumentModal({
  open,
  contractId,
  onClose,
  onSaved,
}: {
  open: boolean
  contractId: number
  onClose(): void
  onSaved(): void
}) {
  const [name, setName] = useState('')
  const [file, setFile] = useState<File | null>(null)

  useEffect(() => {
    if (!open) return
    setName('')
    setFile(null)
  }, [open])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (name.trim() === '' || file === null) return
    void storeContractDocument(contractId, name.trim(), file)
      .then(() => {
        toast('Contract saved!', 'Success')
        onSaved()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
  }

  return (
    <Modal
      id="modalNewContractDocument"
      labelledBy="modalNewContractDocumentLabel"
      open={open}
      onClose={onClose}
      large
      title="New document"
    >
      <form className="form-horizontal labelcustomize" method="post" onSubmit={submit}>
        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
            <span className="color">Name</span>
          </label>
          <div className="col-sm-9">
            <input
              type="text"
              className="form-control"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
        </div>
        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
            <span className="color">File</span>
          </label>
          <div className="col-sm-9">
            <input
              type="file"
              className="form-control"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              required
            />
            <span className="form-text text-muted">Allowed extensions: pdf|jpg|jpeg|png|doc</span>
          </div>
        </div>
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button type="submit" className="btn btn-primary" disabled={name.trim() === '' || file === null}>
            Save
          </button>
        </div>
      </form>
    </Modal>
  )
}

/**
 * `contracts/new/addContractModal.php` + `addContractModal-1.js`.
 *
 * Sablonul ales aduce serviciile lui si hotaraste felul de contract (asa face
 * si `$('#contract_template').on('change')`); MSRP se calculeaza din cantitate
 * x cost cand omul nu l-a scris singur.
 */
function ContractModal({
  contract,
  shell,
  onClose,
  onSaved,
}: {
  contract: ContractRow
  shell: ContractsShell
  onClose(): void
  onSaved(): void
}) {
  const templates = useQuery(contractTemplateChoicesQuery)
  const nextNumber = useQuery({ ...contractNumberQuery, enabled: contract.ID === 0 })
  const [number, setNumber] = useState(contract.contract_number)
  const [templateId, setTemplateId] = useState(contract.contract_template_id)
  const [msrp, setMsrp] = useState(contract.contract_cost)
  const [currency, setCurrency] = useState(contract.contract_currency || 'RON')
  const [startDate, setStartDate] = useState(contract.start_date)
  const [unlimited, setUnlimited] = useState(contract.end_date === '' || contract.end_date.startsWith('0000'))
  const [endDate, setEndDate] = useState(contract.end_date.startsWith('0000') ? '' : contract.end_date)
  const [description, setDescription] = useState(contract.description)
  const [amounts, setAmounts] = useState<Record<number, { quantity: string; cost: string; sell: string }>>({})

  const services = useQuery(
    contractFormServicesQuery(templateId, contract.ID, {
      start: 0,
      length: 200,
      orderColumn: 1,
      orderName: 'service_name',
      orderDir: 'asc',
      search: '',
    }),
  )

  useEffect(() => {
    if (contract.ID === 0 && nextNumber.data) setNumber(String(nextNumber.data.number))
  }, [contract.ID, nextNumber.data])

  useEffect(() => {
    if (!services.data) return
    const next: Record<number, { quantity: string; cost: string; sell: string }> = {}
    for (const row of services.data.data) {
      next[row.service_id] = { quantity: row.quantity, cost: row.cost, sell: row.sell_cost }
    }
    setAmounts(next)
  }, [services.data])

  /* `calc_msrp()`: suma cantitate x cost, cand MSRP nu e scris de om. */
  const computed = useMemo(
    () =>
      Object.values(amounts).reduce((sum, item) => sum + Number(item.quantity || 0) * Number(item.cost || 0), 0),
    [amounts],
  )

  useEffect(() => {
    if (Number(msrp || 0) <= 0 && computed > 0) setMsrp(String(computed))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computed])

  const chosenTemplate = (templates.data?.rows ?? []).find((item) => item.ID === templateId)
  const typeId = chosenTemplate ? chosenTemplate.contract_type : contract.contract_type_id

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const fields: Record<string, string | number> = {
      contract_id: contract.ID,
      company_id: contract.company_id,
      contract_number: number,
      contract_type: typeId,
      contract_template: templateId,
      msrp,
      currency_msrp: currency,
      start_date: startDate,
      end_date: unlimited ? '' : endDate,
      description,
    }
    for (const [serviceId, item] of Object.entries(amounts)) {
      fields[`services_assigned[${serviceId}]`] = 1
      fields[`quantity[${serviceId}]`] = item.quantity
      fields[`cost[${serviceId}]`] = item.cost
      fields[`sell_cost[${serviceId}]`] = item.sell
    }
    void saveContract(fields)
      .then(() => {
        toast('Contract saved', 'Success')
        onSaved()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
  }

  const setAmount = (serviceId: number, key: 'quantity' | 'cost' | 'sell', value: string) => {
    setAmounts((prev) => ({
      ...prev,
      [serviceId]: { ...(prev[serviceId] ?? { quantity: '0', cost: '0', sell: '0' }), [key]: value },
    }))
  }

  return (
    <Modal
      id="modalNewContract"
      labelledBy="modalNewContractLabel"
      open
      onClose={onClose}
      large
      title={contract.ID > 0 ? 'Edit contract' : 'Assign new contract'}
    >
      <form className="form-horizontal labelcustomize" method="post" onSubmit={submit}>
        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
            <span className="color">Customer</span>
          </label>
          <div className="col-sm-9">
            <p className="form-control-static">{contract.company_name}</p>
          </div>
        </div>
        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
            <span className="color">Contract Number</span>
          </label>
          <div className="col-sm-9">
            <input
              type="number"
              className="form-control"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              required
            />
          </div>
        </div>
        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
            <span className="color">Contract Template</span>
          </label>
          <div className="col-sm-9">
            <select
              className="form-control"
              value={templateId}
              onChange={(e) => setTemplateId(Number(e.target.value))}
            >
              <option value={0}> - select - </option>
              {(templates.data?.rows ?? []).map((item) => (
                <option key={item.ID} value={item.ID}>
                  {item.name}
                </option>
              ))}
            </select>
            {chosenTemplate && (
              <span className="help-block">Contract type: {chosenTemplate.contract_type_name}</span>
            )}
          </div>
        </div>

        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
            <span className="color">Services Details</span>
          </label>
          <div className="col-sm-12" style={{ maxHeight: 260, overflow: 'auto' }}>
            <table className="table table-striped table-bordered" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Priority</th>
                  <th>Coverage</th>
                  <th>Response SLA</th>
                  <th>Measurement Unit</th>
                  <th style={{ width: 90 }}>Quantity</th>
                  <th style={{ width: 90 }}>Cost</th>
                  <th style={{ width: 90 }}>Sell Cost</th>
                  <th style={{ width: 60 }}>Currency</th>
                </tr>
              </thead>
              <tbody>
                {(services.data?.data ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={9}>There are no Services assigned to this Contract Template</td>
                  </tr>
                ) : (
                  (services.data?.data ?? []).map((row) => {
                    const item = amounts[row.service_id] ?? { quantity: '0', cost: '0', sell: '0' }
                    return (
                      <tr key={row.service_id}>
                        <td>{row.service_name}</td>
                        <td>{row.priority_name}</td>
                        <td>{row.coverage_name}</td>
                        <td>{row.sla_name}</td>
                        <td>{row.UM_name}</td>
                        <td>
                          <input
                            type="number"
                            className="form-control input-sm"
                            value={item.quantity}
                            onChange={(e) => setAmount(row.service_id, 'quantity', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control input-sm"
                            value={item.cost}
                            onChange={(e) => setAmount(row.service_id, 'cost', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control input-sm"
                            value={item.sell}
                            onChange={(e) => setAmount(row.service_id, 'sell', e.target.value)}
                          />
                        </td>
                        <td>{row.currency}</td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
            <span className="color">Total MSRP</span>
          </label>
          <div className="col-sm-7">
            <input type="number" className="form-control" value={msrp} onChange={(e) => setMsrp(e.target.value)} />
          </div>
          <div className="col-sm-2">
            <select className="form-control" value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {['RON', 'EUR', 'USD'].map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
            <span className="color">Start Date</span>
          </label>
          <div className="col-sm-9">
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
            <span className="color">End Date</span>
          </label>
          <div className="col-sm-9">
            <input
              type="date"
              className="form-control"
              value={endDate}
              disabled={unlimited}
              onChange={(e) => setEndDate(e.target.value)}
            />
            <br />
            <input type="checkbox" checked={unlimited} onChange={(e) => setUnlimited(e.target.checked)} /> Unlimited
          </div>
        </div>

        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
            <span className="color">
              Description <b className="text-danger">*</b>
            </span>
          </label>
          <div className="col-sm-9">
            <textarea
              className="form-control"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </div>
        </div>

        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!shell.canEdit || number === '' || templateId < 1 || startDate === '' || description.trim() === ''}
          >
            Save
          </button>
        </div>
      </form>
    </Modal>
  )
}
